import {useCallback} from "react";
import {useLazyGetDrillholeByIdQuery, useLazyGetDrillholeMineralsQuery, useLazyGetDrillholeXrfQuery} from "../../redux/rtk/drillholeApi";
import {useAppDispatch} from "../../redux/hooks";
import {useSelector} from "react-redux";
import {RootState} from "../../redux/store";
import {setXrfElements, setMineralsElements, setSelectedBoreholes} from "../../redux/slices/ChartStateSlice";
import {parseElementsFromColumns, parseMineralColumnsFromData, transformDrillhole} from "../../functions/drillholeTransform";
import proj4 from "proj4";

export const useDrillholeDetails = () => {
    const [triggerGetById] = useLazyGetDrillholeByIdQuery();
    const [triggerGetMinerals] = useLazyGetDrillholeMineralsQuery();
    const [triggerGetXrf] = useLazyGetDrillholeXrfQuery();
    const projectId = useSelector((state: RootState) => state.project.selectedProject?.project_id);
    const dispatch = useAppDispatch();

    const fetchDetails = useCallback(async (
        drillholes: any[],
        selectLineStart?: number[],
    ) => {
        if (!projectId) return;
        const offsetCoords3857 = selectLineStart
            ? proj4('EPSG:4326', 'EPSG:3857', selectLineStart)
            : undefined;

        const enrichedBoreholes: any[] = [];
        let allXrfElements: any[] = [];
        let allMineralsElements: any[] = [];

        for (const drillhole of drillholes) {
            const holeId = drillhole.hole_id;

            const infoResult = await triggerGetById({projectId, holeId}).unwrap();

            const [mineralsResult, xrfResult] = await Promise.all([
                triggerGetMinerals({projectId, holeId}).unwrap(),
                triggerGetXrf({projectId, holeId}).unwrap(),
            ]);

            // Parse XRF elements from first drillhole
            if (allXrfElements.length === 0 && infoResult.xrf_columns) {
                allXrfElements = parseElementsFromColumns(infoResult.xrf_columns);
                try {
                    const stored = localStorage.getItem('xrfElements');
                    if (stored) {
                        const storedEls = JSON.parse(stored);
                        const activeMap = new Map(storedEls.map((el: any) => [el.id, el.active]));
                        allXrfElements = allXrfElements.map((el: any) => ({
                            ...el,
                            active: activeMap.has(el.id) ? activeMap.get(el.id) : el.active,
                        }));
                    }
                } catch (_) {}
                dispatch(setXrfElements(allXrfElements));
            }

            // Parse minerals elements from first drillhole
            if (allMineralsElements.length === 0 && mineralsResult) {
                allMineralsElements = parseMineralColumnsFromData(mineralsResult);
                try {
                    const stored = localStorage.getItem('mineralsElements');
                    if (stored) {
                        const storedEls = JSON.parse(stored);
                        const activeMap = new Map(storedEls.map((el: any) => [el.name, el.active]));
                        allMineralsElements = allMineralsElements.map((el: any) => ({
                            ...el,
                            active: activeMap.has(el.name) ? activeMap.get(el.name) : el.active,
                        }));
                    }
                } catch (_) {}
                dispatch(setMineralsElements(allMineralsElements));
            }

            const enriched = transformDrillhole(
                drillhole, infoResult, xrfResult, mineralsResult,
                allXrfElements, allMineralsElements, offsetCoords3857,
            );

            enrichedBoreholes.push(enriched);
        }

        dispatch(setSelectedBoreholes(enrichedBoreholes));
    }, [projectId, triggerGetById, triggerGetMinerals, triggerGetXrf, dispatch]);

    return {fetchDetails};
};
